import React, { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import { Form, Progress, Alert } from "reactstrap";
import { CSSTransition } from "react-transition-group";

import { useLocalization } from "../../containers/I18nProvider";
import { WithCaptcha } from '../../containers/captcha';
import fondo from './img/fondo.jpg';
import logoWhite from './img/logo-white.png';

const EyeOpenIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943
         9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
  </svg>
);

const LoginForm = ({ api, location, TurnstileComponent, ...props }) => {
  const nodeRef = useRef(null);
  const { translate, changeDefaultLocale } = useLocalization();

  const [onLogin, setOnLogin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({ open: false, message: "" });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const parameters = queryString.parse(location.search);

  useEffect(() => {
    changeDefaultLocale('es');
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setOnLogin(true);
    setError({ open: false, message: "" });

    api.userPassLogin({ username, password }, parameters)
      .then((response) => {
        setOnLogin(false);
        window.location = parameters.redirect_uri + "?" + queryString.stringify(response);
      })
      .catch((error) => {
        const _translate = translate(error.errorCode);
        let message = translate("RequestLoginErrorMessage");
        if (_translate) {
          message = _translate;
        } else if (error.details) {
          message = error.details;
        }
        setOnLogin(false);
        setError({ open: true, message });
      });
  };

  return (
    <CSSTransition
      in={showForm}
      timeout={300}
      classNames="formshow"
      nodeRef={nodeRef}
      unmountOnExit
    >
      <div className="ifx-wrapper" ref={nodeRef}>

        {/* Brand panel */}
        <aside
          className="ifx-brand-panel"
          style={{ backgroundImage: `url(${fondo})` }}
        >
          <div className="ifx-brand-inner">
            <img src={logoWhite} alt="IFX Networks" className="ifx-brand-logo" />

            <div className="ifx-brand-copy">
              <h1 className="ifx-brand-tagline">
                <span>{translate("BrandHighlight1")}</span> {translate("BrandConnector")}{' '}
                <span>{translate("BrandHighlight2")}</span><br />
                {translate("BrandTaglineEnd")}
              </h1>
              <p className="ifx-brand-subtitle">{translate("BrandSubtitle")}</p>
            </div>

            <a
              className="ifx-brand-link"
              href="https://www.ifxnetworks.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              ifxnetworks.com
            </a>
          </div>
        </aside>

        {/* Login panel */}
        <main className="ifx-login-panel">
          <div className="ifx-login-card">

            <h2 className="ifx-login-title">{translate("ClientAuthentication")}</h2>
            <p className="ifx-login-desc">{translate("LoginDescription")}</p>

            <Form onSubmit={onSubmit}>

              <div className="ifx-form-group">
                <div className="ifx-label-row">
                  <label className="ifx-form-label" htmlFor="ifx-username">
                    {translate("LabelUserName")}
                  </label>
                </div>
                <div className="ifx-input-wrap">
                  <span className="ifx-lead-icon" aria-hidden="true">
                    <UserIcon />
                  </span>
                  <input
                    id="ifx-username"
                    className="ifx-form-input"
                    type="text"
                    required
                    disabled={onLogin}
                    autoCorrect="off"
                    autoCapitalize="none"
                    autoComplete="username"
                    placeholder={translate("PlaceholderUserName")}
                    onChange={e => setUsername(e.target.value)}
                  />
                </div>
              </div>

              <div className="ifx-form-group">
                <div className="ifx-label-row">
                  <label className="ifx-form-label" htmlFor="ifx-password">
                    {translate("LabelPassword")}
                  </label>
                  <a
                    className="ifx-forgot"
                    href="https://portal.ifxnetworks.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {translate("ForgotPassword")}
                  </a>
                </div>
                <div className="ifx-input-wrap">
                  <span className="ifx-lead-icon" aria-hidden="true">
                    <LockIcon />
                  </span>
                  <input
                    id="ifx-password"
                    className="ifx-form-input"
                    type={showPassword ? "text" : "password"}
                    required
                    disabled={onLogin}
                    autoComplete="current-password"
                    placeholder={translate("PlaceholderPassword")}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`ifx-toggle-password${showPassword ? ' is-visible' : ''}`}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    onClick={() => setShowPassword(v => !v)}
                  >
                    {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
              </div>

              <div className="ifx-form-group">
                <TurnstileComponent setTurnstileSuccess={props.setTurnstileSuccess} />
              </div>

              {!onLogin ? (
                <button
                  type="submit"
                  className="ifx-btn-login"
                  disabled={!props.turnstileSuccess}
                >
                  {translate("ButtonEnter")}
                </button>
              ) : (
                <Progress animated color="primary" value={100} />
              )}

            </Form>

            <Alert
              color="danger"
              isOpen={error.open}
              toggle={() => setError({ open: false })}
              className="ifx-error-alert"
            >
              {error.message}
            </Alert>

          </div>
        </main>

      </div>
    </CSSTransition>
  );
};

export default WithCaptcha(LoginForm, { useInsideForm: true, style: { marginTop: '1rem' } });
